/**
 * Push Notifications Service
 *
 * Handles Web Push subscription and management.
 * Works with the service worker for receiving push notifications.
 */

import { supabase } from '../lib/supabase'

// Get VAPID public key from environment
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied'
  }
  return Notification.permission
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported')
    return 'denied'
  }

  const permission = await Notification.requestPermission()
  return permission
}

/**
 * Convert VAPID public key from base64 URL to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Get browser/device name for subscription record
 */
function getDeviceName(): string {
  const ua = navigator.userAgent
  let browserName = 'Unknown'
  let osName = 'Unknown'

  // Detect browser
  if (ua.includes('Firefox/')) {
    browserName = 'Firefox'
  } else if (ua.includes('Edg/')) {
    browserName = 'Edge'
  } else if (ua.includes('Chrome/')) {
    browserName = 'Chrome'
  } else if (ua.includes('Safari/')) {
    browserName = 'Safari'
  }

  // Detect OS
  if (ua.includes('Windows')) {
    osName = 'Windows'
  } else if (ua.includes('Mac OS')) {
    osName = 'Mac'
  } else if (ua.includes('Linux')) {
    osName = 'Linux'
  } else if (ua.includes('Android')) {
    osName = 'Android'
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    osName = 'iOS'
  }

  return `${browserName} på ${osName}`
}

/**
 * Subscribe to push notifications
 * Returns true if subscription was successful
 */
export async function subscribeToPush(): Promise<{
  success: boolean
  error?: string
}> {
  try {
    // Check support
    if (!isPushSupported()) {
      return { success: false, error: 'Push-notiser stöds inte i denna webbläsare' }
    }

    // Check VAPID key
    if (!VAPID_PUBLIC_KEY) {
      console.error('VAPID_PUBLIC_KEY is not configured')
      return { success: false, error: 'Push-notiser är inte konfigurerade' }
    }

    // Request permission
    const permission = await requestNotificationPermission()
    if (permission !== 'granted') {
      return { success: false, error: 'Du måste tillåta notiser för att aktivera push' }
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription()

    // If no subscription, create one
    if (!subscription) {
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })
    }

    // Extract keys from subscription
    const keys = subscription.toJSON().keys
    if (!keys?.p256dh || !keys?.auth) {
      throw new Error('Kunde inte hämta krypteringsnycklar')
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'Du måste vara inloggad' }
    }

    // Save subscription to database
    const { error: dbError } = await supabase
      .from('push_subscriptions')
      .upsert({
        user_id: user.id,
        endpoint: subscription.endpoint,
        p256dh_key: keys.p256dh,
        auth_key: keys.auth,
        device_name: getDeviceName(),
        is_active: true,
        error_count: 0
      }, {
        onConflict: 'user_id,endpoint'
      })

    if (dbError) {
      console.error('Error saving push subscription:', dbError)
      return { success: false, error: 'Kunde inte spara prenumerationen' }
    }

    return { success: true }
  } catch (error) {
    console.error('Error subscribing to push:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<{
  success: boolean
  error?: string
}> {
  try {
    if (!isPushSupported()) {
      return { success: true } // Already not subscribed if not supported
    }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      // Remove from database first
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint)
      }

      // Unsubscribe from push
      await subscription.unsubscribe()
    }

    return { success: true }
  } catch (error) {
    console.error('Error unsubscribing from push:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Check if user is currently subscribed to push
 */
export async function isPushSubscribed(): Promise<boolean> {
  try {
    if (!isPushSupported()) {
      return false
    }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      return false
    }

    // Verify subscription exists in database
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return false
    }

    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', user.id)
      .eq('endpoint', subscription.endpoint)
      .eq('is_active', true)
      .maybeSingle()

    return !error && !!data
  } catch {
    return false
  }
}

/**
 * Get all active push subscriptions for current user
 */
export async function getUserPushSubscriptions(): Promise<Array<{
  id: string
  device_name: string
  created_at: string
  last_used_at: string | null
}>> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return []
    }

    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('id, device_name, created_at, last_used_at')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching subscriptions:', error)
      return []
    }

    return data || []
  } catch {
    return []
  }
}

/**
 * Remove a specific subscription by ID
 */
export async function removePushSubscription(subscriptionId: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'Du måste vara inloggad' }
    }

    // Get the subscription endpoint before deleting
    const { data: subscription } = await supabase
      .from('push_subscriptions')
      .select('endpoint')
      .eq('id', subscriptionId)
      .eq('user_id', user.id)
      .single()

    // Delete from database
    const { error } = await supabase
      .from('push_subscriptions')
      .delete()
      .eq('id', subscriptionId)
      .eq('user_id', user.id)

    if (error) {
      return { success: false, error: 'Kunde inte ta bort prenumerationen' }
    }

    // If this is the current device's subscription, also unsubscribe from push manager
    if (subscription && isPushSupported()) {
      const registration = await navigator.serviceWorker.ready
      const currentSub = await registration.pushManager.getSubscription()

      if (currentSub && currentSub.endpoint === subscription.endpoint) {
        await currentSub.unsubscribe()
      }
    }

    return { success: true }
  } catch (error) {
    console.error('Error removing subscription:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Send a test push notification to current user
 */
export async function sendTestPush(): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'Du måste vara inloggad' }
    }

    const { data, error } = await supabase.functions.invoke('send-push', {
      body: {
        userId: user.id,
        payload: {
          title: 'Testnotis',
          body: 'Push-notiser fungerar!',
          url: '/',
          tag: 'test'
        }
      }
    })

    if (error) {
      console.error('Error sending test push:', error)
      return { success: false, error: error.message }
    }

    if (data?.sent === 0) {
      return { success: false, error: 'Inga aktiva prenumerationer hittades' }
    }

    return { success: true }
  } catch (error) {
    console.error('Error sending test push:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}
